import React, { useMemo } from "react";
import clsx from "clsx";
import { SkeletonImage } from "./SkeletonImage";

type AvatarProps = {
  user: {
    profileImageUrl?: string;
    firstName?: string;
    name?: string;
  };
  onPress?: () => void;
  className?: string;
  size?: number;
  avatarTextSize?: "xs" | "sm" | "base" | "lg" | "xl";
  backgroundColor?: string;
  rounded?: boolean;
};

export const Avatar: React.FC<AvatarProps> = ({
  user,
  onPress,
  size = 30,
  className,
  avatarTextSize = "sm",
  backgroundColor = "bg-butter-cream",
  rounded = false,
}) => {
  const avatarContent = useMemo(
    () => (
      <>
        {user.profileImageUrl ? (
          <SkeletonImage
            src={user.profileImageUrl}
            alt={`${user.firstName || user.name || "User"} avatar`}
            className="h-full w-full"
          />
        ) : (
          <div
            className={clsx(
              "flex items-center justify-center w-full h-full",
              backgroundColor,
            )}
            style={{ width: size, height: size }}
          >
            <span
              className={clsx(
                `text-${avatarTextSize} font-bold text-butter-green`,
                "font-sans",
              )}
            >
              {user.firstName?.charAt(0).toUpperCase() ||
                user.name?.charAt(0).toUpperCase() ||
                ""}
            </span>
          </div>
        )}
      </>
    ),
    [
      user.profileImageUrl,
      user.firstName,
      user.name,
      size,
      avatarTextSize,
      backgroundColor,
    ],
  );

  const containerClasses = clsx(
    "overflow-hidden flex items-center justify-center",
    className,
  );

  // Border radius scales proportionally with avatar size (~18% of size).
  // When `rounded`, render fully circular (used for overlapping stacks).
  const containerStyle = {
    width: size,
    height: size,
    borderRadius: rounded ? "9999px" : Math.round(size * 0.18),
  };

  return onPress ? (
    <button
      className={containerClasses}
      style={containerStyle}
      onClick={onPress}
      type="button"
    >
      {avatarContent}
    </button>
  ) : (
    <div className={containerClasses} style={containerStyle}>
      {avatarContent}
    </div>
  );
};
